import { formatWeight, type Sex, type WeightUnit } from "@babia/checks";
import { useState } from "react";
import { track } from "../../lib/analytics.ts";
import type { Growth } from "../../lib/growth.ts";
import {
  curvePoints,
  type GrowthSpan,
  monthDays,
  nearestPoint,
  percentileCell,
  type PlottedRow,
  readingWords,
  spacedLabels,
  weighDay,
  type WeightScale,
} from "../../lib/growth-chart.ts";
import { useWidth } from "../../lib/use-width.ts";
import { cn } from "../../lib/utils.ts";

const HEIGHT = 260;
const PAD = { top: 10, right: 34, bottom: 36, left: 40 };
/** Percentile labels closer than this, in px, would overlap. */
const LABEL_GAP = 11;

/**
 * One baby's weights on the WHO weight-for-age curves for their sex: nine
 * thin percentile lines (the 50th darker) and the weights joined in the accent
 * color. Plain SVG in theme colors, so it follows night mode with no chart
 * library to download. The x axis is age in days, corrected for a baby born
 * early; the axes come from the caller, shared by every baby. A tap picks the
 * nearest weight for the caption above. Nothing marks a percentile as good or
 * bad: no bands, no colors.
 */
export function GrowthChart({
  growth,
  sex,
  rows,
  span,
  scale,
  unit,
  name,
  now,
}: {
  growth: Growth;
  sex: Sex;
  /** The weights with a place on the curves, oldest first. */
  rows: PlottedRow[];
  span: GrowthSpan;
  scale: WeightScale;
  unit: WeightUnit;
  name: string;
  now: Date;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  const [box, width] = useWidth();
  // The latest weight, until a tap picks another (or a tab switch drops it).
  const selected = rows.find((r) => r.id === picked) ?? rows.at(-1);
  if (!selected) return null;

  const plotW = Math.max(1, width - PAD.left - PAD.right);
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const x = (day: number) => PAD.left + (plotW * day) / span.end;
  const y = (grams: number) => PAD.top + plotH * (1 - (grams - scale.lo) / (scale.hi - scale.lo));
  const path = (points: readonly (readonly [number, number])[]) =>
    points.map(([day, g], i) => `${i ? "L" : "M"}${x(day).toFixed(1)},${y(g).toFixed(1)}`).join("");

  const steps = Math.max(24, Math.round(plotW / 2));
  const curves = growth.PERCENTILE_LINES.flatMap((line) => {
    const points = curvePoints(growth, sex, growth.PERCENTILE_Z[line], span.end, steps);
    const end = points.at(-1);
    return end ? [{ line, d: path(points), endY: y(end[1]) }] : [];
  });
  const labelled = spacedLabels(
    curves.map((c) => ({ line: c.line, y: c.endY })),
    LABEL_GAP,
  );
  const xs = rows.map((r) => x(r.reading.days));
  const corrected = rows.some((r) => r.reading.corrected);
  const caption = (r: PlottedRow) =>
    `${weighDay(r.at, now)} · ${formatWeight(r.grams, unit)} · ${readingWords(r.reading)}`;
  const summary = [
    `${name}'s weights on the WHO weight-for-age curves for ${sex === "female" ? "girls" : "boys"}`,
    ...rows.map(caption),
  ].join(". ");
  const top = scale.ticks.at(-1);

  return (
    <figure className="mt-3 min-w-0">
      <figcaption className="text-[15px] leading-snug tabular-nums" aria-live="polite">
        {caption(selected)}
      </figcaption>
      <div ref={box} className="mt-2">
        <svg
          width={width}
          height={HEIGHT}
          viewBox={`0 0 ${width} ${HEIGHT}`}
          role="img"
          aria-label={summary}
          className="block overflow-visible"
        >
          {scale.ticks.map((t) => (
            <g key={t.grams}>
              <line
                x1={PAD.left}
                x2={PAD.left + plotW}
                y1={y(t.grams)}
                y2={y(t.grams)}
                className="stroke-ink/10"
              />
              <text
                x={PAD.left - 6}
                y={y(t.grams)}
                dy="0.32em"
                textAnchor="end"
                className="fill-current text-[10px] tabular-nums opacity-60"
              >
                {t === top ? `${t.label} ${unit}` : t.label}
              </text>
            </g>
          ))}
          {span.ticks.map((m) => {
            const day = monthDays(m);
            return (
              <g key={m}>
                <line
                  x1={x(day)}
                  x2={x(day)}
                  y1={PAD.top}
                  y2={PAD.top + plotH}
                  className={m === 0 ? "stroke-ink/40" : "stroke-ink/10"}
                />
                <text
                  x={x(day)}
                  y={PAD.top + plotH + 14}
                  textAnchor="middle"
                  className="fill-current text-[10px] tabular-nums opacity-60"
                >
                  {m}
                </text>
              </g>
            );
          })}
          <text
            x={PAD.left + plotW / 2}
            y={HEIGHT - 4}
            textAnchor="middle"
            className="fill-current text-[11px] opacity-70"
          >
            {corrected ? "Corrected age in months" : "Age in months"}
          </text>
          {curves.map((c) => (
            <g key={c.line}>
              <path
                d={c.d}
                fill="none"
                strokeWidth={1}
                className={c.line === 50 ? "stroke-ink/50" : "stroke-ink/15"}
              />
              {labelled.has(c.line) ? (
                <text
                  x={PAD.left + plotW + 4}
                  y={c.endY}
                  dy="0.32em"
                  className={cn(
                    "fill-current text-[10px] tabular-nums",
                    c.line === 50 ? "opacity-70" : "opacity-50",
                  )}
                >
                  {percentileCell(c.line)}
                </text>
              ) : null}
            </g>
          ))}
          <line
            x1={x(selected.reading.days)}
            x2={x(selected.reading.days)}
            y1={PAD.top}
            y2={PAD.top + plotH}
            strokeDasharray="2 3"
            className="pointer-events-none stroke-ink/40"
          />
          {rows.length > 1 ? (
            <path
              d={path(rows.map((r) => [r.reading.days, r.grams]))}
              fill="none"
              strokeWidth={1.5}
              className="pointer-events-none stroke-accent"
            />
          ) : null}
          {rows.map((r) => (
            <circle
              key={r.id}
              cx={x(r.reading.days)}
              cy={y(r.grams)}
              r={r === selected ? 5 : 3}
              strokeWidth={r === selected ? 2 : 0}
              className="pointer-events-none fill-accent stroke-paper"
            />
          ))}
          <rect
            x={PAD.left - 8}
            y={0}
            width={plotW + 16}
            height={HEIGHT}
            fill="transparent"
            className="cursor-pointer"
            onClick={(event) => {
              const left = event.currentTarget.ownerSVGElement?.getBoundingClientRect().left ?? 0;
              const row = rows[nearestPoint(xs, event.clientX - left)];
              if (!row || row === selected) return;
              setPicked(row.id);
              track("chart day selected", { chart: "growth" });
            }}
          />
        </svg>
      </div>
    </figure>
  );
}
